import React, { Component, ReactNode, ComponentType } from 'react';
import { AvatarErrorBoundaryProps, FallbackProps } from './types';
import { ErrorLogger } from './ErrorLogger';

interface AvatarErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
  errorId: string | null;
  isRefreshingAvatar: boolean;
  fallbackAvatar?: string;
}

const AvatarFallback: ComponentType<Omit<FallbackProps, 'errorInfo'> & { isRefreshing: boolean; fallbackAvatar?: string }> = ({
  error,
  resetError,
  errorId,
  retryCount,
  isRefreshing,
  fallbackAvatar
}) => {
  const handleRefreshAvatar = async () => {
    try {
      // Trigger avatar refresh if the function is available globally
      if (typeof window !== 'undefined' && (window as any).refreshUserAvatar) {
        await (window as any).refreshUserAvatar();
      }
      resetError();
    } catch (refreshError) {
      console.error('Failed to refresh avatar:', refreshError);
    }
  };

  const handleUseDefaultAvatar = () => {
    if (typeof window !== 'undefined' && (window as any).setDefaultAvatar) {
      (window as any).setDefaultAvatar();
    }
    resetError();
  };

  return (
    <div className="flex items-center justify-center w-full h-full bg-card rounded-full">
      <div className="text-center p-2">
        {/* Default avatar icon */}
        <div className="w-8 h-8 mx-auto mb-2 bg-border rounded-full flex items-center justify-center">
          <svg
            className="w-4 h-4 text-foreground"
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              fillRule="evenodd"
              d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z"
              clipRule="evenodd"
            />
          </svg>
        </div>

        {/* Show minimal error info in production */}
        {process.env.NODE_ENV === 'development' ? (
          <div className="text-xs text-red-600 mb-2 max-w-[100px] truncate">
            {error.message}
          </div>
        ) : null}

        {/* Action buttons */}
        <div className="flex flex-col gap-1">
          <button
            onClick={handleRefreshAvatar}
            disabled={isRefreshing}
            className="text-xs px-2 py-1 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isRefreshing ? 'Atualizando...' : 'Atualizar'}
          </button>
          <button
            onClick={handleUseDefaultAvatar}
            className="text-xs px-2 py-1 bg-muted text-foreground rounded hover:bg-muted/80 transition-colors"
          >
            Padrão
          </button>
        </div>

        {/* Retry count indicator */}
        {retryCount > 1 && (
          <div className="text-xs text-muted-foreground mt-1">
            Tentativas: {retryCount}/3
          </div>
        )}
      </div>
    </div>
  );
};

export class AvatarErrorBoundary extends Component<AvatarErrorBoundaryProps, AvatarErrorBoundaryState> {
  private errorLogger: ErrorLogger;
  private refreshTimeoutId: NodeJS.Timeout | null = null;

  constructor(props: AvatarErrorBoundaryProps) {
    super(props);

    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: null,
      isRefreshingAvatar: false,
      fallbackAvatar: undefined
    };

    this.errorLogger = ErrorLogger.getInstance();
  }

  static getDerivedStateFromError(error: Error): Partial<AvatarErrorBoundaryState> {
    return {
      hasError: true,
      error,
      errorId: `avatar_err_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
    };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    const errorId = this.state.errorId || this.generateErrorId();

    this.setState({
      error,
      errorInfo,
      errorId
    });

    // Log avatar-specific error
    const loggedErrorId = this.errorLogger.logAvatarError(
      error,
      this.props.context || 'AvatarComponent'
    );

    // Call custom avatar error handler
    if (this.props.onAvatarError) {
      this.props.onAvatarError(error);
    }

    // Call general error handler
    if (this.props.onError) {
      this.props.onError(error, errorInfo, loggedErrorId);
    }

    // Try to set a fallback avatar
    this.setFallbackAvatar();
  }

  private generateErrorId(): string {
    return `avatar_err_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  private setFallbackAvatar(): void {
    // Generate a fallback avatar URL using a service like UI Avatars
    const userName = this.getUserName();
    const fallbackUrl = `https://ui-avatars.com/api/?name=${encodeURIComponent(userName)}&background=6366f1&color=fff&size=128`;

    this.setState({ fallbackAvatar: fallbackUrl });
  }

  private getUserName(): string {
    try {
      // Try to get user name from auth context or localStorage
      const userStr = localStorage.getItem('user');
      if (userStr) {
        const user = JSON.parse(userStr);
        return user.name || user.email || 'User';
      }
    } catch (error) {
      // Ignore errors
    }
    return 'User';
  }

  private refreshAvatar = async (): Promise<void> => {
    if (this.state.isRefreshingAvatar) {
      return;
    }

    this.setState({ isRefreshingAvatar: true });

    try {
      // Wait a bit for the refresh to complete
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Trigger avatar refresh if function is available
      if (typeof window !== 'undefined' && (window as any).refreshUserAvatar) {
        await (window as any).refreshUserAvatar();
      }

      this.resetError();
    } catch (refreshError) {
      console.error('Failed to refresh avatar:', refreshError);
      this.errorLogger.logAvatarError(
        refreshError as Error,
        'AvatarRefresh'
      );
    } finally {
      this.setState({ isRefreshingAvatar: false });
    }
  };

  private resetError = (): void => {
    const maxRetries = this.props.maxRetries || 3;

    if (this.state.retryCount >= maxRetries) {
      console.warn(`Max retries (${maxRetries}) exceeded for avatar error boundary`);
      return;
    }

    // Call custom reset handler
    if (this.props.onReset) {
      this.props.onReset();
    }

    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: null,
      isRefreshingAvatar: false,
      fallbackAvatar: undefined
    });

    // Debounce rapid retries
    if (this.refreshTimeoutId) {
      clearTimeout(this.refreshTimeoutId);
    }

    this.refreshTimeoutId = setTimeout(() => {
      this.refreshTimeoutId = null;
    }, 2000);
  };

  private renderFallback(): ReactNode {
    const { fallback: fallbackProp } = this.props;

    if (React.isValidElement(fallbackProp)) {
      return fallbackProp;
    }

    if (typeof fallbackProp === 'function') {
      const FallbackComponent = fallbackProp as ComponentType<FallbackProps>;
      return (
        <FallbackComponent
          error={this.state.error!}
          errorInfo={this.state.errorInfo!}
          resetError={this.resetError}
          errorId={this.state.errorId!}
          retryCount={0}
        />
      );
    }

    return (
      <AvatarFallback
        error={this.state.error!}
        errorInfo={this.state.errorInfo!}
        resetError={this.resetError}
        errorId={this.state.errorId!}
        retryCount={0}
        isRefreshing={this.state.isRefreshingAvatar}
        fallbackAvatar={this.state.fallbackAvatar}
      />
    );
  }

  render(): ReactNode {
    if (this.state.hasError) {
      return this.renderFallback();
    }

    return this.props.children;
  }

  componentWillUnmount(): void {
    if (this.refreshTimeoutId) {
      clearTimeout(this.refreshTimeoutId);
      this.refreshTimeoutId = null;
    }
  }
}

// Hook for easy avatar error handling
export function useAvatarErrorHandler() {
  const errorLogger = ErrorLogger.getInstance();

  const handleAvatarError = (error: Error, operation: string) => {
    return errorLogger.logAvatarError(error, operation);
  };

  const handleNetworkError = (error: Error, url: string) => {
    return errorLogger.logNetworkError(error, url, 'GET');
  };

  const handleLoadError = (src: string) => {
    const error = new Error(`Failed to load avatar image: ${src}`);
    return handleAvatarError(error, 'ImageLoad');
  };

  return {
    handleAvatarError,
    handleNetworkError,
    handleLoadError
  };
}

// HOC for wrapping avatar components with AvatarErrorBoundary
export function withAvatarErrorBoundary<P extends object>(
  Component: ComponentType<P>,
  avatarErrorBoundaryProps?: Omit<AvatarErrorBoundaryProps, 'children'>
): ComponentType<P> {
  const WrappedComponent = (props: P) => (
    <AvatarErrorBoundary {...avatarErrorBoundaryProps}>
      <Component {...props} />
    </AvatarErrorBoundary>
  );

  WrappedComponent.displayName = `withAvatarErrorBoundary(${Component.displayName || Component.name})`;

  return WrappedComponent;
}